import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { SimpleGrid } from '@chakra-ui/react';

import {
    getBasisDartSpread,
    modifyDartSpread,
    deleteAllDartSpreadData,
    addDartSpreadRows,
    deleteDartSpreadRows,
    multisortPairs,
} from '../../services/basis';

import SubItemData from '../itemData/SubItemData';
import ComboboxFormikField from 'components/forms/ComboboxFormikField';

const DartSpreadTab = ({
    tab,
    name,
    options,
    basis,
    basisList,
    updateBasis,
    onLoadingStart,
    onLoadingEnd,
    actionBarSlot,
}) => {
    const intl = useIntl();

    const gridResource = useMemo(() => {
        return {
            create: (rows) => addDartSpreadRows(basis.basisId, rows),
            read: (pagination) => getBasisDartSpread(basis.basisId, pagination),
            update: (rows) => modifyDartSpread(rows),
            delete: (rows) => {
                const data = rows.map((elem) => elem.id);
                return deleteDartSpreadRows(basis.basisId, data);
            },
            deleteAll: () => deleteAllDartSpreadData(basis.basisId),
        };
    }, [basis.basisId]);

    const handleSelectChange = async (value, type) => {
        const tempBasis = { ...basis };
        if (type === 'daHourly') {
            tempBasis.daHourlyBasisId = value;
        }
        if (type === 'scaling') {
            tempBasis.scalingintervalId = value;
        }
        // for basis configurator/wizard only to do autosave
        updateBasis && updateBasis(tempBasis);
    };

    const columns = useMemo(
        () => [
            {
                field: 'month',
                type: 'number',
                cellEditorParams: { required: false },
                headerName: intl.formatMessage({ id: 'basis_tabs_month' }),
                initialSort: 'asc',
                initialSortIndex: 2,
            },
            {
                field: 'year',
                type: 'number',
                cellEditorParams: { required: false },
                headerName: intl.formatMessage({ id: 'basis_tabs_year' }),
                initialSort: 'asc',
                initialSortIndex: 1,
            },
            {
                field: 'peakPeriodLabelId',
                type: 'select',
                cellEditorParams: { options: options.peakPeriods, required: false },
                headerName: intl.formatMessage({ id: 'basis_tabs_peak_period' }),
                minWidth: 180,
                initialSort: 'asc',
                initialSortIndex: 3,
            },
            {
                field: 'multiplier',
                type: 'number',
                cellEditorParams: { required: false },
                headerName: intl.formatMessage({ id: 'basis_tabs_multiplier' }),
            },
            {
                field: 'adder',
                type: 'number',
                cellEditorParams: { required: false },
                headerName: intl.formatMessage({ id: 'basis_tabs_adder' }),
            },
        ],
        [intl, options.peakPeriods]
    );

    const phrasing = useMemo(
        () => ({
            create: {
                content: <FormattedMessage id="common_add_rows_sub_heading_without_date" />,
            },
        }),
        []
    );

    return (
        <>
            <SimpleGrid pb={5} columns={{ xl: 4 }} spacingX={6} spacingY={4}>
                <ComboboxFormikField
                    id="scalingintervalId"
                    name="scalingintervalId"
                    label={intl.formatMessage({ id: 'basis_tabs_scaling_interval' })}
                    options={options.scalingIntervals}
                    labelKey="description"
                    valueKey="id"
                    onChange={(value) => handleSelectChange(value, 'scaling')}
                    showPlaceholder={true}
                    placeholderValue={null}
                />

                <ComboboxFormikField
                    id="daHourlyBasisId"
                    name="daHourlyBasisId"
                    label={intl.formatMessage({ id: 'basis_tabs_da_hourly_basis' })}
                    options={basisList}
                    labelKey="description"
                    valueKey="id"
                    onChange={(value) => handleSelectChange(value, 'daHourly')}
                    showPlaceholder={true}
                    placeholderValue={null}
                />
            </SimpleGrid>

            <SubItemData
                actionBarSlot={() => actionBarSlot && actionBarSlot}
                tab={tab}
                name={name}
                onLoadingStart={onLoadingStart}
                onLoadingEnd={onLoadingEnd}
                gridResource={gridResource}
                columns={columns}
                addDataColumns={columns}
                multisortPairs={multisortPairs}
                phrasing={phrasing}
            />
        </>
    );
};

export default DartSpreadTab;
